import { useMemo } from 'react';
import { ArrowLeft, BookOpenText, Clock, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ActionButtons } from '@/features/email-detail/components/ActionButtons';
import { MobileActionBar } from '@/features/email-detail/components/MobileActionBar';
import { useSenderGroupingStore } from '@/stores/senderGroupingStore';
import { useMoveToFolderStore } from '@/stores/moveToFolderStore';
import type { ActiveSenderGroup, SenderGroupInfo, SenderGroupQuickAction } from '../types';
import type { SenderGroupEntry } from '../hooks/useSenderGrouping';

interface SenderGroupHeaderProps {
  activeGroup: ActiveSenderGroup | null | undefined;
  activeSenderData: SenderGroupEntry | null;
  activeSenderInfo: SenderGroupInfo | null;
  senderGroupAction?: 'archive' | 'delete' | 'block' | 'approve' | null;
  isSenderDigestLoading?: boolean;
  isSenderDigestVisible?: boolean;
  onSenderArchiveAll?: (group: SenderGroupInfo) => Promise<void> | void;
  onSenderDeleteAll?: (group: SenderGroupInfo) => Promise<void> | void;
  onSenderGroupAction?: (group: SenderGroupInfo, action: SenderGroupQuickAction) => Promise<void> | void;
  onShowDigest?: (group: SenderGroupInfo) => void;
  onExitSenderGroup?: () => void;
}

export function SenderGroupHeader({
  activeGroup,
  activeSenderData,
  activeSenderInfo,
  senderGroupAction = null,
  isSenderDigestLoading = false,
  isSenderDigestVisible = false,
  onSenderArchiveAll,
  onSenderDeleteAll,
  onSenderGroupAction,
  onShowDigest,
  onExitSenderGroup,
}: SenderGroupHeaderProps) {
  // Read store's activeSenderGroupInfo.totalMessages which is updated after fetching
  const storeGroupInfo = useSenderGroupingStore((state) => state.activeSenderGroupInfo);

  const badgeInfo = useMemo(() => {
    if (!activeSenderData && !activeSenderInfo) {
      return null;
    }
    // Priority order for totalMessages:
    // 1. Store's activeSenderGroupInfo.totalMessages (updated after fetch to actual count)
    // 2. activeSenderInfo.totalMessages (from hook, computed from emails)
    // 3. activeSenderData.totalMessages (from email's senderMailboxCount)
    // 4. Fallback to email count
    const totalFromStore =
      typeof storeGroupInfo?.totalMessages === 'number' && Number.isFinite(storeGroupInfo.totalMessages)
        ? storeGroupInfo.totalMessages
        : null;
    const totalFromInfo =
      typeof activeSenderInfo?.totalMessages === 'number' && Number.isFinite(activeSenderInfo.totalMessages)
        ? activeSenderInfo.totalMessages
        : null;
    const totalFromData =
      typeof activeSenderData?.totalMessages === 'number' && Number.isFinite(activeSenderData.totalMessages)
        ? activeSenderData.totalMessages
        : null;
    const fallbackLength = activeSenderData?.emails.length ?? activeSenderInfo?.emails.length ?? 0;
    // Use the best available total, but ensure it's at least the loaded email count
    // so the badge updates when new emails arrive via realtime events
    const bestTotal = totalFromStore ?? totalFromInfo ?? totalFromData ?? fallbackLength;
    const resolvedTotal = Math.max(bestTotal, fallbackLength);
    if (!resolvedTotal) {
      return null;
    }
    return { label: String(resolvedTotal), variant: 'secondary' as const };
  }, [storeGroupInfo, activeSenderData, activeSenderInfo]);

  if (!activeSenderData) {
    return null;
  }

  const actionsBlocked = senderGroupAction !== null || isSenderDigestLoading;
  const archiveDisabled = !activeSenderInfo || !onSenderArchiveAll || actionsBlocked;
  const deleteDisabled = !activeSenderInfo || !onSenderDeleteAll || actionsBlocked;

  const handleArchiveAll = async () => {
    if (!activeSenderInfo || !onSenderArchiveAll || actionsBlocked) {
      return;
    }
    try {
      await onSenderArchiveAll(activeSenderInfo);
    } catch (error) {
      console.error('Failed to archive sender messages', error);
    }
  };

  const handleDeleteAll = async () => {
    if (!activeSenderInfo || !onSenderDeleteAll || actionsBlocked) {
      return;
    }
    try {
      await onSenderDeleteAll(activeSenderInfo);
    } catch (error) {
      console.error('Failed to delete sender messages', error);
    }
  };

  // Same visibility rules as the group rows: approve for pending/blocked
  // senders, block for pending only.
  const contactState = activeSenderData.contactApprovalState;
  const canApproveSender = Boolean(
    onSenderGroupAction && activeSenderInfo && (contactState === 'pending' || contactState === 'blocked'),
  );
  const canBlockSender = Boolean(onSenderGroupAction && activeSenderInfo && contactState === 'pending');

  const handleQuickAction = (action: SenderGroupQuickAction) => {
    if (!activeSenderInfo || !onSenderGroupAction || actionsBlocked) {
      return;
    }
    void onSenderGroupAction(activeSenderInfo, action);
  };

  // Same semantics as the group rows: the toggle reflects whether anything is
  // still unread across the loaded messages.
  const groupAllRead = activeSenderData.unreadCount === 0;
  const markDisabled = !activeSenderInfo || !onSenderGroupAction || actionsBlocked;
  const handleMarkAll = (_emailId: string, read: boolean) => {
    handleQuickAction(read ? 'markRead' : 'markUnread');
  };

  const groupEmailIds = activeSenderInfo?.emails.map((email) => email.id) ?? [];
  const handleMoveAll = () => {
    if (groupEmailIds.length) {
      useMoveToFolderStore.getState().open(groupEmailIds);
    }
  };

  return (
    <>
    <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 sm:px-4 py-3">
      <div className="flex items-center gap-2 min-w-0">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onExitSenderGroup?.()}
          className="-ml-1 flex-shrink-0"
          disabled={!onExitSenderGroup}
        >
          <ArrowLeft className="mr-1 mt-0.5 h-4 w-4" />
          <span className="hidden sm:inline">Back</span>
        </Button>
        <span className="truncate text-sm font-medium">
          {activeGroup?.name || activeGroup?.email || activeSenderData.displayEmail}
        </span>
        {badgeInfo && (
          <Badge variant={badgeInfo.variant} className="px-2 py-0 text-xs flex-shrink-0">
            {badgeInfo.label}
          </Badge>
        )}
        {activeSenderData.contactApprovalState &&
          activeSenderData.contactApprovalState !== 'approved' && (
            <Tooltip>
              <TooltipTrigger asChild>
                {activeSenderData.contactApprovalState === 'blocked' ? (
                  <Ban className="h-3.5 w-3.5 flex-shrink-0 text-destructive" />
                ) : (
                  <Clock className="h-3.5 w-3.5 flex-shrink-0 text-warning-ink" />
                )}
              </TooltipTrigger>
              <TooltipContent>
                {activeSenderData.contactApprovalState === 'blocked' ? 'Blocked sender' : 'Pending approval'}
              </TooltipContent>
            </Tooltip>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* Desktop: the standard action row, scoped to the whole group.
            Below md these actions live in the mobile action bar instead. */}
        <ActionButtons
          emailId={activeSenderData.displayEmail}
          onArchive={() => void handleArchiveAll()}
          archiveDisabled={archiveDisabled}
          archivePending={senderGroupAction === 'archive'}
          onDelete={() => void handleDeleteAll()}
          deleteDisabled={deleteDisabled}
          deletePending={senderGroupAction === 'delete'}
          isRead={groupAllRead}
          onMarkRead={handleMarkAll}
          markDisabled={markDisabled}
          onMoveToFolder={handleMoveAll}
          moveDisabled={markDisabled || !groupEmailIds.length}
          onApproveSender={canApproveSender ? () => handleQuickAction('approveSender') : undefined}
          onBlockSender={canBlockSender ? () => handleQuickAction('blockSender') : undefined}
          tooltips={{
            archive: 'Archive all messages from this sender',
            delete: 'Delete all messages from this sender',
            markRead: 'Mark all read',
            markUnread: 'Mark all unread',
            move: 'Move all to folder',
          }}
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                if (!onShowDigest || !activeSenderInfo) {
                  return;
                }
                onShowDigest(activeSenderInfo);
              }}
              disabled={
                !onShowDigest ||
                !activeSenderInfo ||
                senderGroupAction !== null ||
                isSenderDigestVisible ||
                isSenderDigestLoading
              }
              className="flex items-center gap-1"
            >
              <BookOpenText className="h-4 w-4" />
              <span className="hidden sm:inline">Digest</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent className="sm:hidden">Digest</TooltipContent>
        </Tooltip>
      </div>
    </div>
      {/* Mobile: group-level actions live in the standard action bar (no
          reply for a sender group; mark/move/approve/block in the overflow). */}
      <MobileActionBar
        emailId={activeSenderData.displayEmail}
        onArchive={() => void handleArchiveAll()}
        archiveDisabled={archiveDisabled}
        onDelete={() => void handleDeleteAll()}
        deleteDisabled={deleteDisabled}
        isRead={groupAllRead}
        onMarkRead={handleMarkAll}
        onMoveToFolder={groupEmailIds.length ? handleMoveAll : undefined}
        onApproveSender={canApproveSender ? () => handleQuickAction('approveSender') : undefined}
        onBlockSender={canBlockSender ? () => handleQuickAction('blockSender') : undefined}
      />
    </>
  );
}
